import { _ } from "../../../core/polyglot/polyglot.js";
import { menuBar, wrapItem } from "prosemirror-menu";
import { setBlockType } from "prosemirror-commands";
import AbstractAction from "./AbstractAction.js";
import OrderedMenuItem from "../OrderedMenuItem.js";

/**
 * Action adding a menu bar to the editor.
 * It takes care to collect all the menu items from the other actions.
 * It also provides the MenuItems for the basic nodes and marks supported
 * out-of-the-box by the editor.
 */
export default class MenuAction extends AbstractAction {
	getPlugins({ actionsManager, schema }) {
		const content = actionsManager.buildMenuItems({
			schema,
			defaultMenuItems: [
				...this._buildMenuItems(schema.marks),
				...this._buildNodeItems(schema.nodes),
			],
		});

		return [
			menuBar({
				floating: true,
				content: content.map((each) => [each]),
			}),
		];
	}

	_buildMenuItems(marks) {
		return [this._makeStrongItem(marks.strong), this._makeEmItem(marks.em)];
	}

	_makeStrongItem(mark) {
		return this._makeMarkItem({
			order: 10,
			title: _("Toggle bold style"),
			icon: this._makeIcon({ cssClass: "fa fa-bold" }),
			mark,
		});
	}

	_makeEmItem(mark) {
		return this._makeMarkItem({
			order: 20,
			title: _("Toggle italic"),
			icon: this._makeIcon({ cssClass: "fa fa-italic" }),
			mark,
		});
	}

	_buildNodeItems(nodes) {
		return [
			this._buildTypeItem(nodes),
			this._buildQuoteItem(nodes.blockquote),
		];
	}

	_buildTypeItem(nodes) {
		return new OrderedMenuItem({
			order: 40,
			menuItem: this._makeDropdownMenuItem({
				items: [
					this._makeParagraphItem(nodes.paragraph),
					this._makeHeaderItem({ heading: nodes.heading, level: 2 }),
					this._makeHeaderItem({ heading: nodes.heading, level: 3 }),
				],
				label: _("Type...").toString(),
				cssClass: "prosemirror-type",
			}),
		});
	}

	_makeParagraphItem(node) {
		return this._ensureItemCanBeEnabled({
			title: _("Change to paragraph").toString(),
			iconSpecs: { klass: "fa fa-align-left" },
			run: setBlockType(node),
		});
	}

	_makeHeaderItem({ heading, level }) {
		return this._ensureItemCanBeEnabled({
			title: _("Change to heading {level}", { level }).toString(),
			iconSpecs: { klass: "fa fa-header", text: level },
			run: setBlockType(heading, { level }),
		});
	}

	_buildQuoteItem(node) {
		return new OrderedMenuItem({
			order: 50,
			menuItem: wrapItem(node, {
				title: _("Wrap in block quote"),
				icon: this._makeIcon({ cssClass: "fa fa-quote-right" }),
			}),
		});
	}
}
